<template>
    <b-modal
        v-model="modalShow"
        size="lg"
        hide-header
        hide-footer
        @hidden="close">
        <div
            v-if="consultation !== null"
            class="modal-body">
            <div class="text-center modalHeading">内容をご確認のうえ、予約を確定してください。</div>
            <div class="card mb-3">
                <div class="card-header">予約内容のご確認</div>
                <div class="card-body">
                    <div class="modalContent">
                        <section class="modalSection">
                            <div class="reservationTimes">
                                <div>予約日時</div>
                                <div class="u-palt">
                                    <span>{{ helper.dateForHuman(consultation.start_time, user.timezone) }}</span>
                                    <span :class="helper.dayForHumanCSS(consultation.start_time, user.timezone)">
                                        {{ helper.dayForHuman(consultation.start_time, user.timezone, { bracket: true }) }}
                                    </span>
                                    <span class="d-inline-block d-md-inline mt-1 mb-1 ml-0 mt-md-0 mb-md-0 ml-md-1">
                                        {{ `${helper.timeForHuman(consultation.start_time, user.timezone)} - ${helper.timeForHuman(consultation.end_time, user.timezone)}` }}
                                    </span>
                                    <span class="ml-0 ml-md-1">
                                        {{ `（${doctorMenu.consultation_minutes}分）` }}
                                    </span>
                                </div>
                                <div
                                    v-if="user.timezone !== 'Asia/Tokyo'"
                                    class="vc-timezone-box u-palt">
                                    <span>{{ helper.dateForHuman(consultation.start_time, 'Asia/Tokyo') }}</span>
                                    <span :class="helper.dayForHumanCSS(consultation.start_time, 'Asia/Tokyo')">
                                        {{ helper.dayForHuman(consultation.start_time, 'Asia/Tokyo', { bracket: true }) }}
                                    </span>
                                    <span class="ml-1">{{ `${helper.timeForHuman(consultation.start_time, 'Asia/Tokyo')} - ${helper.timeForHuman(consultation.end_time, 'Asia/Tokyo')}（日本時間）` }}</span>
                                </div>
                            </div>
                        </section>

                        <hr class="divide">

                        <section class="modalSection modalSectionPaddingTop">
                            <doctor-menu-base-info
                                :menu="doctorMenu"
                                :user="user">
                            </doctor-menu-base-info>
                        </section>
                    </div>
                </div>
            </div>

            <div
                v-if="isPayPrice"
                class="card mb-3">
                <div class="card-header">お支払い方法</div>
                <div class="card-body">
                    <dl class="mb-0">
                        <dd class="mb-0">
                            <span>{{ paymentMethod }}</span>
                        </dd>
                    </dl>
                </div>
            </div>

            <div class="mt-4 mb-3 text-center">上記の内容で予約します。</div>

            <div class="btnWrapper d-flex justify-content-center">
                <b-button
                    type="button"
                    variant="yokumiru-gray"
                    class="mt-2 mb-3 ml-2 mr-2"
                    :disabled="inProgress"
                    @click="onClickPending">
                    後で確認する
                </b-button>
                <b-button
                    type="button"
                    variant="yokumiru-user"
                    class="mt-2 mb-3 ml-2 mr-2"
                    :disabled="inProgress"
                    @click="onClickOk">
                    {{ inProgress ? '処理中です…' : '予約を確定' }}
                </b-button>
            </div>
            <div
                v-if="errors.stripe"
                class="invalid-feedback d-flex justify-content-center mt-3 mb-3">
                <!-- @see app/Exceptions/Handler.php -->
                <div v-if="errors.stripe.code === 'card_declined'">カード会社によって支払いが拒否されました。</div>
                <div v-else-if="errors.stripe.type === 'card_error'">このカードは利用できません。</div>
                <div v-else-if="['api_connection_error', 'api_error'].includes(errors.stripe.type)">
                    決済システムのメンテナンス中です。時間を置いて再度お試しください。
                </div>
            </div>

            <hr class="divide divideStrong">

            <div class="card mt-4 mb-3">
                <div class="card-header">ご予約にあたってのお願い</div>
                <div class="card-body">
                    <div class="vc-notice">
                        <p>医師もスケジュールを空けて待機しておりますが、急患などの止むを得ない対応により、ご連絡もできず{{ $locale.consultation_host }}時間に遅れる、もしくは入室出来ない可能性がございます。</p>
                        <template v-if="$store.state.configs.subscription == 1">
                            <p>会員様が予約時刻に「{{ $locale.consultation_host }}室」に入室して、{{ $store.state.configs.consultation_absence_min_doctor }}分間お待ちいただいても医師の入室がなく{{ $locale.consultation_host }}が開始されない場合は、自動的にオンライン{{ $locale.consultation }}が終了し、{{ $locale.consultation_host }}予約は「キャンセル」されます。</p>
                            <p>医師の都合により{{ $locale.consultation }}がキャンセルされた場合には、{{ $locale.consultation_host }}料やキャンセル料は一切必要ありません。</p>
                            <p class="mb-0">
                                クレジットカードでお支払いただいた場合の{{ $locale.consultation_host }}料は、自動で返金処理が行われます。返金されるタイミングは、ご利用されたカード会社を介しての返金となるため、カード会社の締め日や規定により異なりますが、10日以内には処理がされます。詳細の確認をご希望の場合は、お使いのカード会社にお問い合わせください。
                            </p>
                        </template>
                        <template v-else>
                            <p>予約時間の5分経過後も医師の入室がなかった場合は、お手数ですがチャットに「{{ $locale.consultation_host }}時間に待機していたが、医師の入室がなかった」旨の記入をお願いいたします。</p>
                            <p class="mb-0">医師の都合によりオンライン{{ $locale.consultation_host }}ができなった場合は、{{ $locale.consultation_guest }}料やキャンセル料は発生いたしません。</p>
                        </template>
                    </div>
                </div>
            </div>

            <template v-if="isDoctorPass">
                <div
                    v-if="$store.state.configs.consultation_cancel_policy_doctor_pass !== null
                    && $store.state.configs.consultation_cancel_policy_doctor_pass !== ''"
                    class="card mb-3">
                    <div class="card-header">予約のキャンセルについて</div>
                    <div class="card-body">
                        <div
                            class="vc-notice vc-notice--pre-wrap"
                            v-html="$store.state.configs.consultation_cancel_policy_doctor_pass">
                        </div>
                    </div>
                </div>
            </template>
            <template v-else-if="isFlatRate">
                <div
                    v-if="$store.state.configs.consultation_cancel_policy_flat_rate !== null
                    && $store.state.configs.consultation_cancel_policy_flat_rate !== ''"
                    class="card mb-3">
                    <div class="card-header">予約のキャンセルについて</div>
                    <div class="card-body">
                        <div
                            class="vc-notice vc-notice--pre-wrap"
                            v-html="$store.state.configs.consultation_cancel_policy_flat_rate">
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <div
                    v-if="$store.state.configs.consultation_cancel_policy !== null
                    && $store.state.configs.consultation_cancel_policy !== ''"
                    class="card mb-3">
                    <div class="card-header">予約のキャンセルについて</div>
                    <div class="card-body">
                        <div
                            class="vc-notice vc-notice--pre-wrap"
                            v-html="$store.state.configs.consultation_cancel_policy">
                        </div>
                    </div>
                </div>
            </template>

            <div class="mt-4 mb-3 text-center">上記の内容で予約します。</div>

            <div class="btnWrapper d-flex justify-content-center">
                <b-button
                    type="button"
                    variant="yokumiru-gray"
                    class="mt-2 mb-3 ml-2 mr-2"
                    :disabled="inProgress"
                    @click="close">
                    後で確認する
                </b-button>
                <b-button
                    type="button"
                    variant="yokumiru-user"
                    class="mt-2 mb-3 ml-2 mr-2"
                    :disabled="inProgress"
                    @click="onClick">
                    {{ inProgress ? '処理中です…' : '予約を確定' }}
                </b-button>
            </div>
            <div
                v-if="errors.stripe"
                class="invalid-feedback d-flex justify-content-center mt-3 mb-3">
                <!-- @see app/Exceptions/Handler.php -->
                <div v-if="errors.stripe.code === 'card_declined'">カード会社によって支払いが拒否されました。</div>
                <div v-else-if="errors.stripe.type === 'card_error'">このカードは利用できません。</div>
                <div v-else-if="['api_connection_error', 'api_error'].includes(errors.stripe.type)">
                    決済システムのメンテナンス中です。時間を置いて再度お試しください。
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import constants from '../../../constants'
import YokumiruHelper from '../../../YokumiruHelper'
import { loadStripe } from '@stripe/stripe-js'

export default {
    props: {
        user: {
            type: Object,
            required: true,
        },
        stripePubkey: {
            type: String | null,
            default: null,
        },
    },

    data: function () {
        return {
            helper: YokumiruHelper,
            modalShow: false,
            inProgress: false,
            consultation: null,
            card: null,
            stripe: null,
            errors: {},
        }
    },

    computed: {
        doctorMenu: function () {
            return this.consultation ? this.consultation.doctor_menu : null
        },

        /**
         * ドクターパス利用中かどうか
         * @return {*|boolean}
         */
        isDoctorPass: function () {
            return this.user && this.helper.isDoctorPass(this.user.plan)
        },

        /**
         * 定額プラン利用中かどうか
         * @return {*|boolean}
         */
        isFlatRate: function () {
            return this.user && this.helper.isFlatRatePlan(this.user.plan)
        },

        /**
         * 相談料の決済が必要かどうか
         * @return {boolean}
         */
        isPayPrice: function () {
            if (!this.consultation) return false

            if (this.$locale.type === 'consul' && this.consultation.estimated_total < 1) {
                return false
            }
            if (this.doctorMenu && this.doctorMenu.is_health_package) {
                return false
            }
            return this.user && this.user.plan.is_pay_consultation_price
        },

        /**
         * 支払い方法の表示
         * @return {string}
         */
        paymentMethod: function () {
            if (!this.consultation) return ''

            const pm = this.consultation.payment_method

            if (pm.id === constants.payment_methods.credit && this.card) {
                return `${pm.name}　**** **** **** ${this.card.last4}`
            }
            return pm.name
        },
    },

    created: function () {
        this.fetchCreditCard()
        bus.$on('open-modal-user-draft-consultation-confirm', this.open)
    },

    beforeDestroy: function () {
        bus.$off('open-modal-user-draft-consultation-confirm')
    },

    methods: {
        /**
         * モーダル表示
         * @param {object} consultation
         */
        open: function (consultation) {
            this.consultation = consultation
            this.modalShow = true
        },

        /**
         * モーダル非表示
         */
        close: function () {
            this.errors = {}
            this.modalShow = false
        },

        /**
         * カード取得
         */
        fetchCreditCard: function () {
            if (this.card !== null) return

            axios.get(constants.user.credit_cards.endpoint.base)
                .then(({data}) => {
                    this.card = data.data
                })
        },

        /**
         * 「後で確認する」ボタン押下時
         */
        onClickPending: function () {
            bus.$emit('user-draft-consultation-notifier-closed')
            this.close()
        },

        /**
         * 「予約を確定」ボタン押下時
         */
        onClickOk: async function () {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = constants.user.consultations.endpoint.base
            await axios.put(`${endpoint}/${this.consultation.id}/accept`)
                .then(async ({data}) => {
                    if (data.data.client_secret === null) {
                        await axios.delete(`${endpoint}/${this.consultation.id}/draft`)
                        bus.$emit('user-draft-reservation-accepted')
                        this.close()
                        this.inProgress = false
                    }
                    else {
                        this.stripe = await loadStripe(this.stripePubkey, { locale: 'ja' })

                        // 3DS認証フロー
                        // @see https://github.com/sewii/Yokumiru/issues/987
                        this.stripe
                            .confirmCardPayment(data.data.client_secret, {
                                payment_method: data.data.payment_method,
                            })
                            .then(async (result) => {
                                if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
                                    await axios.delete(`${endpoint}/${this.consultation.id}/draft`)
                                    bus.$emit('user-draft-reservation-accepted')
                                    this.close()
                                }
                                else {
                                    bus.$emit('alert-danger', '3Dセキュア認証がキャンセルされました。')
                                }
                                this.inProgress = false
                            })
                    }
                })
                .catch(({response}) => {
                    if (
                        response &&
                        response.hasOwnProperty('data') &&
                        response.data.hasOwnProperty('errors')
                    ) {
                        this.errors = response.data.errors
                        if (this.errors.message) {
                            bus.$emit('alert-danger', this.errors.message[0])
                            this.inProgress = false
                            return
                        }
                        else if (this.errors.stripe) {
                            bus.$emit('alert-danger', '登録中のクレジットカードはご利用いただけません。エラー内容をご確認ください。')
                            this.inProgress = false
                            return
                        }
                    }
                    bus.$emit('alert-danger', 'システムエラーが発生しました。時間を置いて再度お試しください。')
                    this.inProgress = false
                })
        },
    },
}
</script>

<style scoped lang="scss">
.modalHeading {
    padding-bottom: 20px;
    margin-bottom: 20px;
    font-size: 18px;
    border-bottom: 1px solid #a5a5a5;
}

.divide {
    &.divideStrong {
        border-top: 1px solid #a5a5a5;
    }
}

.btnWrapper {
    .btn {
        height: 45px;
        min-width: 120px;
        padding-top: 10px;
        padding-bottom: 10px;
        border: none;
    }
}

.reservationTimes {
    margin-bottom: 20px;
    color: #2A2E31;
    line-height: 1.1;

    div {
        &:nth-child(1) {
            margin-bottom: 6px;
            font-size: 14px;
            font-weight: bold;
            line-height: 1.1;
        }

        &:nth-child(2) {
            margin-bottom: 4px;
            font-size: 18px;
            font-weight: bold;

            span {
                &:nth-child(2) {
                    font-size: 17px;
                    font-weight: normal;
                }

                &:last-child {
                    font-size: 17px;
                    font-weight: normal;
                }
            }
        }

        &:nth-child(3) {
            font-size: 14px;

            span {
                &:nth-child(2) {
                    font-size: 13px;
                }
            }
        }
    }
}
</style>
